import { useState, useEffect, useRef } from 'react';
import './App.css';
import MaskEditor from './MaskEditor';

function App() {
  const [apiUrl, setApiUrl] = useState(() => {
    return localStorage.getItem('apiUrl') || '';
  });
  const [token, setToken] = useState(() => {
    return localStorage.getItem('token') || '';
  });
  const [apiFormat, setApiFormat] = useState(() => {
    return localStorage.getItem('apiFormat') || 'A1111';
  });
  const [steps, setSteps] = useState(() => {
    return parseInt(localStorage.getItem('steps')) || 25;
  });
  const [cfg, setCfg] = useState(() => {
    return parseFloat(localStorage.getItem('cfg')) || 7.5;
  });
  const [samplerName, setSamplerName] = useState(() => {
    return localStorage.getItem('samplerName') || 'euler';
  });
  const [scheduler, setScheduler] = useState(() => {
    return localStorage.getItem('scheduler') || 'simple';
  });
  const [denoise, setDenoise] = useState(() => {
    return parseFloat(localStorage.getItem('denoise')) || 1;
  });
  const [fluxGuidance, setFluxGuidance] = useState(() => {
    return parseFloat(localStorage.getItem('fluxGuidance')) || 3.5;
  });
  const [imageBase64, setImageBase64] = useState(null);
  const [maskBase64, setMaskBase64] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [savedPrompts, setSavedPrompts] = useState(() => {
    return JSON.parse(localStorage.getItem('savedPrompts')) || [];
  });
  const [promptName, setPromptName] = useState('');
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [responseImage, setResponseImage] = useState(null);
  const [error, setError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [postRequestTimestamp, setPostRequestTimestamp] = useState(null);
  const [elapsedSincePost, setElapsedSincePost] = useState(0);
  const [runcomfyDeploymentId, setRuncomfyDeploymentId] = useState(() => {
    return localStorage.getItem('runcomfyDeploymentId') || '';
  });
  const [runcomfyResultUrl, setRuncomfyResultUrl] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [jobHistory, setJobHistory] = useState(() => {
    return JSON.parse(localStorage.getItem('jobHistory')) || [];
  });
  const timerRef = useRef(null);
  const pasteBoxRef = useRef(null);
  const jobHistoryPollRef = useRef(null);

  // Save apiUrl to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('apiUrl', apiUrl);
  }, [apiUrl]);

  // Save token to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('token', token);
  }, [token]);

  // Save apiFormat to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('apiFormat', apiFormat);
  }, [apiFormat]);

  // Save steps to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('steps', steps);
  }, [steps]);

  // Save cfg to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cfg', cfg);
  }, [cfg]);

  // Save samplerName to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('samplerName', samplerName);
  }, [samplerName]);

  // Save scheduler to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('scheduler', scheduler);
  }, [scheduler]);

  // Save denoise to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('denoise', denoise);
  }, [denoise]);

  // Save fluxGuidance to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('fluxGuidance', fluxGuidance);
  }, [fluxGuidance]);

  // Save savedPrompts to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('savedPrompts', JSON.stringify(savedPrompts));
  }, [savedPrompts]);

  // Save runcomfyDeploymentId to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('runcomfyDeploymentId', runcomfyDeploymentId);
  }, [runcomfyDeploymentId]);

  // Save jobHistory to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('jobHistory', JSON.stringify(jobHistory));
  }, [jobHistory]);

  // Update elapsed time since POST request
  useEffect(() => {
    if (postRequestTimestamp && jobId) {
      const updateElapsed = () => {
        const now = Date.now();
        const elapsed = Math.floor((now - postRequestTimestamp) / 1000);
        setElapsedSincePost(elapsed);
      };
      
      updateElapsed();
      const interval = setInterval(updateElapsed, 1000);
      
      return () => clearInterval(interval);
    }
  }, [postRequestTimestamp, jobId]);

  // Timer effect
  useEffect(() => {
    if (loading) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loading]);

  // Handle image paste
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        const reader = new FileReader();
        
        reader.onload = (event) => {
          setImageBase64(event.target.result);
          setError(null);
        };
        
        reader.readAsDataURL(blob);
        return;
      }
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const generateRandomSeed = () => {
    return Math.floor(Math.random() * 100000) + 1;
  };

  const handleSavePrompt = () => {
    if (!promptName.trim()) {
      setError('Please enter a name for this prompt');
      return;
    }

    if (!prompt.trim()) {
      setError('Please enter a prompt to save');
      return;
    }

    const newPrompt = {
      id: Date.now(),
      name: promptName,
      prompt: prompt,
      negativePrompt: negativePrompt,
      createdAt: new Date().toLocaleString()
    };

    setSavedPrompts([...savedPrompts, newPrompt]);
    setPromptName('');
    setError(null);
  };

  const handleLoadPrompt = (savedPrompt) => {
    setPrompt(savedPrompt.prompt);
    setNegativePrompt(savedPrompt.negativePrompt);
  };

  const handleDeletePrompt = (id) => {
    setSavedPrompts(savedPrompts.filter(p => p.id !== id));
  };

  const addJobToHistory = (jobIdValue, apiFormatValue) => {
    const newJob = {
      id: jobIdValue,
      apiFormat: apiFormatValue,
      timestamp: new Date().toLocaleString(),
      resultImage: null,
      status: 'IN_QUEUE'
    };
    setJobHistory([newJob, ...jobHistory]);
  };

  const deleteJobFromHistory = (jobIdValue) => {
    setJobHistory(jobHistory.filter(job => job.id !== jobIdValue));
  };

  // Poll job history every 5 seconds
  useEffect(() => {
    const pollJobHistoryFn = async () => {
      setJobHistory(prevJobHistory => {
        if (prevJobHistory.length === 0) return prevJobHistory;

        (async () => {
          for (const historyJob of prevJobHistory) {
            // Skip if already completed
            if (historyJob.resultImage) continue;

            try {
              let statusUrl;
              
              if (historyJob.apiFormat === 'RunComfy-Qwen' && runcomfyDeploymentId) {
                statusUrl = `https://api.runcomfy.net/prod/v2/deployments/${runcomfyDeploymentId}/requests/${historyJob.id}/result`;
              } else if (apiUrl.trim()) {
                statusUrl = `${apiUrl.replace('/run', '')}/status/${historyJob.id}`;
              } else {
                continue;
              }

              const response = await fetch(statusUrl, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });

              if (!response.ok) continue;

              const data = await response.json();
              let imageUrl = null;

              // Try RunComfy format first (outputs object with node IDs as keys)
              if (data.outputs && typeof data.outputs === 'object') {
                for (const nodeId in data.outputs) {
                  const nodeOutput = data.outputs[nodeId];
                  if (nodeOutput.images && Array.isArray(nodeOutput.images) && nodeOutput.images.length > 0) {
                    const imageData = nodeOutput.images[0];
                    if (typeof imageData === 'object' && imageData.url) {
                      imageUrl = imageData.url;
                      break;
                    }
                  }
                }
              }

              // Try standard output format
              if (!imageUrl && data.output?.images && Array.isArray(data.output.images) && data.output.images.length > 0) {
                const imageData = data.output.images[0];
                if (typeof imageData === 'string' && (imageData.startsWith('http') || imageData.startsWith('/'))) {
                  imageUrl = imageData;
                } else if (typeof imageData === 'object' && imageData.data) {
                  setJobHistory(prevHistory => prevHistory.map(job => 
                    job.id === historyJob.id ? { ...job, resultImage: `data:image/png;base64,${imageData.data}`, status: data.status || 'COMPLETED' } : job
                  ));
                  continue;
                } else if (typeof imageData === 'string') {
                  if (imageData.startsWith('data:image')) {
                    setJobHistory(prevHistory => prevHistory.map(job => 
                      job.id === historyJob.id ? { ...job, resultImage: imageData, status: data.status || 'COMPLETED' } : job
                    ));
                    continue;
                  } else {
                    imageUrl = imageData;
                  }
                }
              } else if (!imageUrl && data.output?.url) {
                imageUrl = data.output.url;
              } else if (!imageUrl && data.image_url) {
                imageUrl = data.image_url;
              }

              // If we have an image URL, fetch and convert to base64
              if (imageUrl) {
                try {
                  const imgResponse = await fetch(imageUrl, { 
                    mode: 'cors',
                    credentials: 'omit'
                  });
                  if (imgResponse.ok) {
                    const blob = await imgResponse.blob();
                    const reader = new FileReader();
                    
                    // Add timeout for FileReader
                    const readerTimeout = setTimeout(() => {
                      console.warn('FileReader timeout - setting image URL as fallback');
                      setJobHistory(prevHistory => prevHistory.map(job => 
                        job.id === historyJob.id ? { ...job, resultImage: imageUrl, status: data.status || 'COMPLETED' } : job
                      ));
                    }, 5000);
                    
                    reader.onload = (e) => {
                      clearTimeout(readerTimeout);
                      setJobHistory(prevHistory => prevHistory.map(job => 
                        job.id === historyJob.id ? { ...job, resultImage: e.target.result, status: data.status || 'COMPLETED' } : job
                      ));
                    };
                    
                    reader.onerror = () => {
                      clearTimeout(readerTimeout);
                      console.error('FileReader error - setting image URL as fallback');
                      setJobHistory(prevHistory => prevHistory.map(job => 
                        job.id === historyJob.id ? { ...job, resultImage: imageUrl, status: data.status || 'COMPLETED' } : job
                      ));
                    };
                    
                    reader.readAsDataURL(blob);
                  } else {
                    console.warn(`Failed to fetch image: ${imgResponse.status} - setting URL as fallback`);
                    setJobHistory(prevHistory => prevHistory.map(job => 
                      job.id === historyJob.id ? { ...job, resultImage: imageUrl, status: data.status || 'COMPLETED' } : job
                    ));
                  }
                } catch (err) {
                  console.error('Failed to download image:', err, '- setting URL as fallback');
                  // Fallback: set the URL directly if base64 conversion fails
                  setJobHistory(prevHistory => prevHistory.map(job => 
                    job.id === historyJob.id ? { ...job, resultImage: imageUrl, status: data.status || 'COMPLETED' } : job
                  ));
                }
              }

              if (data.outputs || data.output?.images || imageUrl) {
                setJobHistory(prevHistory => prevHistory.map(job => 
                  job.id === historyJob.id ? { ...job, status: 'COMPLETED' } : job
                ));
              } else {
                setJobHistory(prevHistory => prevHistory.map(job => 
                  job.id === historyJob.id ? { ...job, status: data.status || 'PROCESSING' } : job
                ));
              }
            } catch (err) {
              console.error('Error polling job history:', err);
            }
          }
        })();

        return prevJobHistory;
      });
    };

    jobHistoryPollRef.current = setInterval(() => {
      pollJobHistoryFn();
    }, 5000);

    return () => {
      if (jobHistoryPollRef.current) clearInterval(jobHistoryPollRef.current);
    };
  }, [apiUrl, token, runcomfyDeploymentId]);

  // Combine image and mask into single PNG with alpha channel
  const combineImageWithMask = async (imageBase64, maskBase64) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const img = new Image();
      const maskImg = new Image();
      let imagesLoaded = 0;

      img.onload = () => {
        imagesLoaded++;
        if (imagesLoaded === 2) {
          // Set canvas size to image size
          canvas.width = img.width;
          canvas.height = img.height;

          // Draw image
          ctx.drawImage(img, 0, 0);

          // Get image data
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

          // Draw mask on separate canvas to get pixel data
          const maskCanvas = document.createElement('canvas');
          maskCanvas.width = maskImg.width;
          maskCanvas.height = maskImg.height;
          const maskCtx = maskCanvas.getContext('2d');
          maskCtx.drawImage(maskImg, 0, 0);
          const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);

          // Apply mask to alpha channel
          for (let i = 0; i < imageData.data.length; i += 4) {
            // Get the grayscale value from mask (any channel works, using red)
            const maskPixelIndex = (i / 4) * 4;
            if (maskPixelIndex < maskData.data.length) {
              const maskValue = maskData.data[maskPixelIndex]; // Red channel as mask
              imageData.data[i + 3] = maskValue; // Set alpha to mask value
            }
          }

          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        }
      };

      maskImg.onload = () => {
        imagesLoaded++;
        if (imagesLoaded === 2) {
          // Same logic as above
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

          const maskCanvas = document.createElement('canvas');
          maskCanvas.width = maskImg.width;
          maskCanvas.height = maskImg.height;
          const maskCtx = maskCanvas.getContext('2d');
          maskCtx.drawImage(maskImg, 0, 0);
          const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);

          for (let i = 0; i < imageData.data.length; i += 4) {
            const maskPixelIndex = (i / 4) * 4;
            if (maskPixelIndex < maskData.data.length) {
              const maskValue = maskData.data[maskPixelIndex];
              imageData.data[i + 3] = maskValue;
            }
          }

          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        }
      };

      img.src = imageBase64;
      maskImg.src = maskBase64;
    });
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        if (item.types.includes('image/png') || item.types.includes('image/jpeg') || item.types.includes('image/webp')) {
          const imageBlob = await item.getType('image/png').catch(() => 
            item.getType('image/jpeg').catch(() => 
              item.getType('image/webp')
            )
          );
          const reader = new FileReader();
          reader.onload = (event) => {
            setImageBase64(event.target.result);
            setError(null);
          };
          reader.readAsDataURL(imageBlob);
          return;
        }
      }
      setError('No image found in clipboard');
    } catch (err) {
      setError('Failed to read clipboard. Make sure you have an image copied.');
    }
  };

  // Generate ComfyUI Workflow
  const generateComfyUIWorkflow = async () => {
    const isI2I = apiFormat === 'ComfyUI-i2i';
    const isInpaint = apiFormat === 'ComfyUI-Inpaint';
    const isQwen = apiFormat === 'ComfyUI-Qwen';
    
    let workflow = {
      "6": {
        "inputs": {
          "text": prompt,
          "clip": ["30", 1]
        },
        "class_type": "CLIPTextEncode",
        "_meta": {
          "title": "CLIP Text Encode (Positive Prompt)"
        }
      },
      "8": {
        "inputs": {
          "samples": ["31", 0],
          "vae": ["30", 2]
        },
        "class_type": "VAEDecode",
        "_meta": {
          "title": "VAE Decode"
        }
      },
      "9": {
        "inputs": {
          "filename_prefix": "ComfyUI",
          "images": ["8", 0]
        },
        "class_type": "SaveImage",
        "_meta": {
          "title": "Save Image"
        }
      },
      "30": {
        "inputs": {
          "ckpt_name": "flux1-dev-fp8.safetensors"
        },
        "class_type": "CheckpointLoaderSimple",
        "_meta": {
          "title": "Load Checkpoint"
        }
      },
      "31": {
        "inputs": {
          "seed": generateRandomSeed(),
          "steps": steps,
          "cfg": cfg,
          "sampler_name": samplerName,
          "scheduler": scheduler,
          "denoise": denoise,
          "model": ["30", 0],
          "positive": ["35", 0],
          "negative": ["33", 0],
          "latent_image": isI2I ? ["13", 0] : ["27", 0]
        },
        "class_type": "KSampler",
        "_meta": {
          "title": "KSampler"
        }
      },
      "33": {
        "inputs": {
          "text": negativePrompt,
          "clip": ["30", 1]
        },
        "class_type": "CLIPTextEncode",
        "_meta": {
          "title": "CLIP Text Encode (Negative Prompt)"
        }
      },
      "35": {
        "inputs": {
          "guidance": fluxGuidance,
          "conditioning": ["6", 0]
        },
        "class_type": "FluxGuidance",
        "_meta": {
          "title": "FluxGuidance"
        }
      },
      "38": {
        "inputs": {
          "images": ["8", 0]
        },
        "class_type": "PreviewImage",
        "_meta": {
          "title": "Preview Image"
        }
      },
      "40": {
        "inputs": {
          "filename_prefix": "ComfyUI",
          "images": ["8", 0]
        },
        "class_type": "SaveImage",
        "_meta": {
          "title": "Save Image"
        }
      }
    };

    // Add nodes for inpainting workflow
    if (isInpaint) {
      workflow["2"] = {
        "inputs": {
          "text": prompt,
          "clip": ["15", 1]
        },
        "class_type": "CLIPTextEncode",
        "_meta": {
          "title": "CLIPTextEncode"
        }
      };

      workflow["3"] = {
        "inputs": {
          "text": negativePrompt,
          "clip": ["15", 1]
        },
        "class_type": "CLIPTextEncode",
        "_meta": {
          "title": "CLIPTextEncode"
        }
      };

      workflow["4"] = {
        "inputs": {
          "image": "input_image"
        },
        "class_type": "LoadImage",
        "_meta": {
          "title": "LoadImage"
        }
      };

      workflow["5"] = {
        "inputs": {
          "positive": ["2", 0],
          "negative": ["3", 0],
          "vae": ["16", 0],
          "pixels": ["4", 0],
          "mask": ["4", 1],
          "noise_mask": true
        },
        "class_type": "InpaintModelConditioning",
        "_meta": {
          "title": "InpaintModelConditioning"
        }
      };

      workflow["6"] = {
        "inputs": {
          "model": ["15", 0],
          "positive": ["5", 0],
          "negative": ["5", 1],
          "latent_image": ["5", 2],
          "seed": generateRandomSeed(),
          "steps": steps,
          "cfg": cfg,
          "sampler_name": samplerName,
          "scheduler": scheduler,
          "denoise": denoise
        },
        "class_type": "KSampler",
        "_meta": {
          "title": "KSampler"
        }
      };

      workflow["15"] = {
        "inputs": {
          "ckpt_name": "epicrealism-inpainting-v5.safetensors"
        },
        "class_type": "CheckpointLoaderSimple",
        "_meta": {
          "title": "CheckpointLoaderSimple"
        }
      };

      workflow["16"] = {
        "inputs": {
          "vae_name": "vae_model.safetensors"
        },
        "class_type": "VAELoader",
        "_meta": {
          "title": "VAELoader"
        }
      };

      // Override nodes 8 and 10
      workflow["8"] = {
        "inputs": {
          "images": ["10", 0],
          "filename_prefix": "ComfyUI"
        },
        "class_type": "SaveImage",
        "_meta": {
          "title": "SaveImage"
        }
      };

      workflow["10"] = {
        "inputs": {
          "samples": ["6", 0],
          "vae": ["16", 0]
        },
        "class_type": "VAEDecode",
        "_meta": {
          "title": "VAEDecode"
        }
      };
    } else if (isI2I) {
      workflow["11"] = {
        "inputs": {
          "image": "input_image"
        },
        "class_type": "LoadImage",
        "_meta": {
          "title": "Load Image"
        }
      };

      workflow["12"] = {
        "inputs": {
          "width": 1024,
          "height": 1024,
          "upscale_method": "lanczos",
          "crop": "center",
          "image": ["11", 0]
        },
        "class_type": "ImageScale",
        "_meta": {
          "title": "Scale Image"
        }
      };

      workflow["13"] = {
        "inputs": {
          "pixels": ["12", 0],
          "vae": ["30", 2]
        },
        "class_type": "VAEEncode",
        "_meta": {
          "title": "VAE Encode"
        }
      };
    } else if (isQwen) {
      // Qwen image editing workflow
      workflow = {
        "8": {
          "inputs": {
            "samples": ["121", 1],
            "vae": ["118", 2]
          },
          "class_type": "VAEDecode",
          "_meta": {
            "title": "VAEDecode"
          }
        },
        "66": {
          "inputs": {
            "model": ["103", 0],
            "shift": 3
          },
          "class_type": "ModelSamplingAuraFlow",
          "_meta": {
            "title": "ModelSamplingAuraFlow"
          }
        },
        "75": {
          "inputs": {
            "model": ["66", 0],
            "strength": 1
          },
          "class_type": "CFGNorm",
          "_meta": {
            "title": "CFGNorm"
          }
        },
        "77": {
          "inputs": {
            "clip": ["103", 1],
            "vae": ["118", 2],
            "image": ["78", 0],
            "prompt": "ugly, blurry, distorted, artifacts, bad, wrong, low quality, anime, digital art, semirealistic, cartoon, manga, drawing, fake, unreal, "
          },
          "class_type": "TextEncodeQwenImageEdit",
          "_meta": {
            "title": "TextEncodeQwenImageEdit"
          }
        },
        "78": {
          "inputs": {
            "image": "cluster_00_0969.jpg"
          },
          "class_type": "LoadImage",
          "_meta": {
            "title": "LoadImage"
          }
        },
        "88": {
          "inputs": {
            "pixels": ["93", 0],
            "vae": ["118", 2]
          },
          "class_type": "VAEEncode",
          "_meta": {
            "title": "VAEEncode"
          }
        },
        "93": {
          "inputs": {
            "image": ["78", 0],
            "upscale_method": "lanczos",
            "megapixels": 1,
            "resolution_steps": 1
          },
          "class_type": "ImageScaleToTotalPixels",
          "_meta": {
            "title": "ImageScaleToTotalPixels"
          }
        },
        "102": {
          "inputs": {
            "images": ["8", 0],
            "metadata": ["106", 0],
            "filename": "%time_%basemodelname_%seed",
            "path": "qwen_edit/%date",
            "extension": "webp",
            "lossless_webp": false,
            "quality_jpeg_or_webp": 100,
            "optimize_png": false,
            "embed_workflow": true,
            "save_workflow_as_json": false,
            "counter": 0,
            "time_format": "%Y-%m-%d-%H%M%S",
            "show_preview": true
          },
          "class_type": "Image Saver Simple",
          "_meta": {
            "title": "Image Saver Simple"
          }
        },
        "103": {
          "inputs": {
            "model": ["118", 0],
            "clip": ["118", 1]
          },
          "class_type": "Power Lora Loader (rgthree)",
          "_meta": {
            "title": "Power Lora Loader (rgthree)"
          }
        },
        "104": {
          "inputs": {
            "any_input": ["118", 0],
            "id": 0,
            "widget_name": "ckpt_name",
            "return_all": false,
            "node_title": "",
            "allowed_float_decimals": 2
          },
          "class_type": "WidgetToString",
          "_meta": {
            "title": "WidgetToString"
          }
        },
        "106": {
          "inputs": {
            "modelname": ["104", 0],
            "seed_value": ["117", 0],
            "steps": ["115", 0],
            "positive": "unknown",
            "negative": "unknown",
            "width": 512,
            "height": 512,
            "cfg": 1,
            "sampler_name": "euler",
            "scheduler_name": "beta57",
            "denoise": 1,
            "clip_skip": 0,
            "additional_hashes": "",
            "download_civitai_data": true,
            "easy_remix": true,
            "custom": ""
          },
          "class_type": "Image Saver Metadata",
          "_meta": {
            "title": "Image Saver Metadata"
          }
        },
        "115": {
          "inputs": {
            "value": 8
          },
          "class_type": "INTConstant",
          "_meta": {
            "title": "Steps"
          }
        },
        "117": {
          "inputs": {
            "value": 0
          },
          "class_type": "PrimitiveInt",
          "_meta": {
            "title": "Seed"
          }
        },
        "118": {
          "inputs": {
            "ckpt_name": "Qwen-Rapid-AIO-NSFW-v11.4.safetensors"
          },
          "class_type": "CheckpointLoaderSimple",
          "_meta": {
            "title": "CheckpointLoaderSimple"
          }
        },
        "119": {
          "inputs": {
            "clip": ["103", 1],
            "vae": ["118", 2],
            "image1": ["78", 0],
            "prompt": prompt
          },
          "class_type": "TextEncodeQwenImageEditPlus",
          "_meta": {
            "title": "TextEncodeQwenImageEditPlus"
          }
        },
        "121": {
          "inputs": {
            "model": ["75", 0],
            "positive": ["119", 0],
            "negative": ["77", 0],
            "latent_image": ["88", 0],
            "steps": ["115", 0],
            "seed": ["117", 0]
          },
          "class_type": "ClownsharKSampler_Beta",
          "_meta": {
            "title": "ClownsharKSampler_Beta"
          }
        }
      };
    } else {
      // Add empty latent for text-to-image
      workflow["27"] = {
        "inputs": {
          "width": 1024,
          "height": 1024,
          "batch_size": 1
        },
        "class_type": "EmptySD3LatentImage",
        "_meta": {
          "title": "EmptySD3LatentImage"
        }
      };
    }

    // Add input images if available
    const inputImages = [];
    if (imageBase64) {
      if (isInpaint && maskBase64) {
        // Combine image and mask into single PNG with alpha channel
        const combinedImage = await combineImageWithMask(imageBase64, maskBase64);
        inputImages.push({
          name: "input_image",
          image: combinedImage.replace(/^data:image\/\w+;base64,/, '')
        });
      } else {
        inputImages.push({
          name: "input_image",
          image: imageBase64.replace(/^data:image\/\w+;base64,/, '')
        });
      }
    }

    return { workflow, inputImages };
  };

  const handleGenerateImage = async () => {
    if (!apiUrl.trim()) {
      setError('Please enter an API URL');
      return;
    }

    if (!token.trim()) {
      setError('Please enter an API token');
      return;
    }

    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    if ((apiFormat === 'ComfyUI-i2i' || apiFormat === 'ComfyUI-Inpaint' || apiFormat === 'ComfyUI-Qwen' || apiFormat === 'RunComfy-Qwen') && !imageBase64) {
      setError('Please paste an image for this API format');
      return;
    }

    if (apiFormat === 'ComfyUI-Inpaint' && !maskBase64) {
      setError('Please create a mask for inpainting');
      return;
    }

    if (apiFormat === 'RunComfy-Qwen' && !runcomfyDeploymentId.trim()) {
      setError('Please enter your RunComfy Deployment ID');
      return;
    }

    setLoading(true);
    setElapsedTime(0);
    setError(null);
    setResponseImage(null);

    try {
      let payload;
      let requestUrl = apiUrl;

      if (apiFormat === 'RunComfy-Qwen') {
        // RunComfy API Format
        const imageString = imageBase64.startsWith('data:') ? imageBase64 : `data:image/jpeg;base64,${imageBase64}`;
        payload = {
          overrides: {
            "78": {
              "inputs": {
                "image": imageString
              }
            },
            "118": {
              "inputs": {
                "ckpt_name": "Qwen-Rapid-AIO-NSFW-v11.1.safetensors"
              }
            },
            "119": {
              "inputs": {
                "prompt": prompt
              }
            },
            "106": {
                "inputs": {
                    // Levágjuk a köteléket a hibás 104-es node-ról, és fix szöveget adunk neki
                    "modelname": "Qwen-Rapid-AIO-NSFW-v11.1.safetensors" 
                }
            }
          }
        };
        // Use RunComfy API endpoint
        requestUrl = `https://api.runcomfy.net/prod/v2/deployments/${runcomfyDeploymentId}/inference`;
      } else if (apiFormat === 'ComfyUI' || apiFormat === 'ComfyUI-i2i' || apiFormat === 'ComfyUI-Inpaint' || apiFormat === 'ComfyUI-Qwen') {
        // ComfyUI API Format (text-to-image, image-to-image, or inpainting)
        const { workflow, inputImages } = await generateComfyUIWorkflow();
        payload = {
          input: {
            workflow: workflow,
            images: inputImages
          }
        };
      } else {
        // A1111 API Format (default)
        payload = {
          input: {
            prompt: prompt,
            negative_prompt: negativePrompt,
            height: 1024,
            width: 1024,
            num_inference_steps: steps,
            refiner_inference_steps: 50,
            guidance_scale: cfg,
            strength: 0.3,
            high_noise_frac: 0.8,
            seed: generateRandomSeed(),
            scheduler: scheduler,
            num_images: 1,
            image_url: imageBase64 || null
          }
        };
      }

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      // Store the job ID from the response
      if (apiFormat === 'RunComfy-Qwen' && data.request_id) {
        setJobId(data.request_id);
        setRuncomfyResultUrl(data.result_url);
        setPollingStatus(data.status || 'IN_QUEUE');
        setPostRequestTimestamp(Date.now());
        addJobToHistory(data.request_id, apiFormat);
      } else if (data.id) {
        setJobId(data.id);
        setPollingStatus(data.status || 'IN_QUEUE');
        setPostRequestTimestamp(Date.now());
        addJobToHistory(data.id, apiFormat);
      }
      // If response has immediate result, display it
      if (data.output?.message && typeof data.output.message === 'string' && data.output.message.startsWith('data:image')) {
        setResponseImage(data.output.message);
      } else if (data.output?.images && Array.isArray(data.output.images) && data.output.images.length > 0) {
        const imageData = data.output.images[0];
        // Check if it's an object with a 'data' property (RunPod format)
        if (typeof imageData === 'object' && imageData.data) {
          setResponseImage(`data:image/png;base64,${imageData.data}`);
        } else if (typeof imageData === 'string') {
          // Check if it already has data:image prefix
          if (imageData.startsWith('data:image')) {
            setResponseImage(imageData);
          } else {
            setResponseImage(`data:image/png;base64,${imageData}`);
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  const handlePollStatus = async () => {
    if (!jobId || !apiUrl.trim() || !token.trim()) {
      setError('Job ID or API configuration missing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let statusUrl;
      
      if (apiFormat === 'RunComfy-Qwen' && runcomfyResultUrl) {
        // Use the result_url provided by RunComfy API response
        statusUrl = runcomfyResultUrl;
      } else if (apiFormat === 'RunComfy-Qwen') {
        // Fallback: construct the result URL if not stored
        statusUrl = `https://api.runcomfy.net/prod/v2/deployments/${runcomfyDeploymentId}/requests/${jobId}/result`;
      } else {
        // Extract the base URL from apiUrl (remove endpoint if present)
        // Assuming apiUrl is like https://api.runpod.ai/v2/{runpod_id}/run
        // We need to construct the status URL like https://api.runpod.ai/v2/{runpod_id}/status/{job_id}
        statusUrl = `${apiUrl.replace('/run', '')}/status/${jobId}`;
      }

      const response = await fetch(statusUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setPollingStatus(data.status || 'UNKNOWN');

      // Handle different response formats
      let imageUrl = null;
      
      // Try RunComfy format first (outputs object with node IDs as keys)
      if (data.outputs && typeof data.outputs === 'object') {
        // Find the first node with images
        for (const nodeId in data.outputs) {
          const nodeOutput = data.outputs[nodeId];
          if (nodeOutput.images && Array.isArray(nodeOutput.images) && nodeOutput.images.length > 0) {
            const imageData = nodeOutput.images[0];
            if (typeof imageData === 'object' && imageData.url) {
              imageUrl = imageData.url;
              break;
            }
          }
        }
      }
      
      // Try standard output format
      if (!imageUrl && data.output?.images && Array.isArray(data.output.images) && data.output.images.length > 0) {
        const imageData = data.output.images[0];
        // Check if it's a URL string
        if (typeof imageData === 'string' && (imageData.startsWith('http') || imageData.startsWith('/'))) {
          imageUrl = imageData;
        } else if (typeof imageData === 'object' && imageData.data) {
          setResponseImage(`data:image/png;base64,${imageData.data}`);
        } else if (typeof imageData === 'string') {
          // Check if it already has data:image prefix
          if (imageData.startsWith('data:image')) {
            setResponseImage(imageData);
          } else {
            setResponseImage(`data:image/png;base64,${imageData}`);
          }
        }
      } else if (!imageUrl && data.output?.url) {
        // RunComfy might return URL in different location
        imageUrl = data.output.url;
      } else if (!imageUrl && data.image_url) {
        imageUrl = data.image_url;
      }

      // If we have an image URL, fetch and convert to base64
      if (imageUrl && !responseImage) {
        try {
          const imgResponse = await fetch(imageUrl, {
            mode: 'cors',
            credentials: 'omit'
          });
          if (imgResponse.ok) {
            const blob = await imgResponse.blob();
            const reader = new FileReader();
            
            // Add timeout for FileReader
            const readerTimeout = setTimeout(() => {
              console.warn('FileReader timeout - setting image URL as fallback');
              setResponseImage(imageUrl);
            }, 5000);
            
            reader.onload = (e) => {
              clearTimeout(readerTimeout);
              setResponseImage(e.target.result);
            };
            
            reader.onerror = () => {
              clearTimeout(readerTimeout);
              console.error('FileReader error - setting image URL as fallback');
              setResponseImage(imageUrl);
            };
            
            reader.readAsDataURL(blob);
          } else {
            console.warn(`Failed to fetch image: ${imgResponse.status} - setting URL as fallback`);
            setResponseImage(imageUrl);
          }
        } catch (err) {
          console.error('Failed to download image:', err);
          // If download fails, still set the URL as the response
          setResponseImage(imageUrl);
        }
      }

      if (data.outputs || data.output?.images || data.output?.url || data.image_url || imageUrl) {
        setJobId(null);
        setPollingStatus(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to check status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 bg-light py-4">
      <div className="container">
        <div className="row">
          <div className="col-12 col-md-8 mx-auto">
            {/* Header */}
            <div className="mb-5">
              <h1 className="display-5 fw-bold mb-2">Image Generator</h1>
              <p className="text-muted">Paste an image and describe what you want to generate</p>
            </div>

            {/* API Configuration */}
            <div className="card mb-4 border-0 bg-white">
              <div className="card-body">
                <h5 className="card-title mb-3">API Configuration</h5>

                <div className="mb-3">
                  <label htmlFor="apiFormat" className="form-label fw-bold">
                    API Format
                  </label>
                  <select
                    id="apiFormat"
                    className="form-select"
                    value={apiFormat}
                    onChange={(e) => setApiFormat(e.target.value)}
                  >
                    <option value="A1111">A1111 (Stable Diffusion)</option>
                    <option value="ComfyUI">ComfyUI (FLUX.1 Text-to-Image)</option>
                    <option value="ComfyUI-i2i">ComfyUI (FLUX.1 Image-to-Image)</option>
                    <option value="ComfyUI-Inpaint">ComfyUI (Inpainting with Mask)</option>
                    <option value="ComfyUI-Qwen">ComfyUI (Qwen Image Editing)</option>
                    <option value="RunComfy-Qwen">RunComfy (Qwen Image Editing)</option>
                  </select>
                  <small className="text-muted d-block mt-2">
                    Choose the API format that your endpoint uses
                  </small>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="apiUrl" className="form-label fw-bold">
                    API Endpoint URL
                  </label>
                  <input
                    id="apiUrl"
                    type="url"
                    className="form-control"
                    value={apiUrl}
                    onChange={(e) => setApiUrl(e.target.value)}
                    placeholder="https://api.example.com/generate"
                  />
                  <small className="text-muted d-block mt-2">
                    Enter the full URL to your image generation API
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="token" className="form-label fw-bold">
                    API Token
                  </label>
                  <input
                    id="token"
                    type="password"
                    className="form-control"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Your API Bearer token"
                  />
                  <small className="text-muted d-block mt-2">
                    Your API key (sent as Authorization: Bearer header)
                  </small>
                </div>

                {apiFormat === 'RunComfy-Qwen' && (
                  <>
                    <div className="mb-3">
                      <label htmlFor="runcomfyDeploymentId" className="form-label fw-bold">
                        RunComfy Deployment ID
                      </label>
                      <input
                        id="runcomfyDeploymentId"
                        type="text"
                        className="form-control"
                        value={runcomfyDeploymentId}
                        onChange={(e) => setRuncomfyDeploymentId(e.target.value)}
                        placeholder="e.g., 5c4dd180-ac78-488f-8195-52b8a787f354"
                      />
                      <small className="text-muted d-block mt-2">
                        Your RunComfy deployment ID
                      </small>
                    </div>

                    <div className="mb-0">
                      <label htmlFor="manualJobId" className="form-label fw-bold">
                        Manual Job ID (Optional)
                      </label>
                      <input
                        id="manualJobId"
                        type="text"
                        className="form-control mb-2"
                        placeholder="e.g., 6f734c8f-8558-42ff-b587-72b774340268"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const jobIdValue = e.target.value.trim();
                            if (jobIdValue) {
                              setJobId(jobIdValue);
                              setRuncomfyResultUrl(`https://api.runcomfy.net/prod/v2/deployments/${runcomfyDeploymentId}/requests/${jobIdValue}/result`);
                              addJobToHistory(jobIdValue, 'RunComfy-Qwen');
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => {
                          const input = document.getElementById('manualJobId');
                          const jobIdValue = input.value.trim();
                          if (jobIdValue) {
                            setJobId(jobIdValue);
                            setRuncomfyResultUrl(`https://api.runcomfy.net/prod/v2/deployments/${runcomfyDeploymentId}/requests/${jobIdValue}/result`);
                            addJobToHistory(jobIdValue, 'RunComfy-Qwen');
                            input.value = '';
                          } else {
                            setError('Please enter a job ID');
                          }
                        }}
                      >
                        Load Job
                      </button>
                      <small className="text-muted d-block mt-2">
                        Enter a job ID from a previous request to check its status
                      </small>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Generation Parameters */}
            <div className="card mb-4 border-0 bg-white">
              <div className="card-body">
                <h5 className="card-title mb-3">Generation Parameters</h5>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="steps" className="form-label fw-bold">
                      Steps
                    </label>
                    <input
                      id="steps"
                      type="number"
                      className="form-control"
                      value={steps}
                      onChange={(e) => setSteps(parseInt(e.target.value) || 0)}
                      min="1"
                      max="100"
                    />
                    <small className="text-muted d-block mt-2">Number of inference steps</small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="cfg" className="form-label fw-bold">
                      CFG Scale
                    </label>
                    <input
                      id="cfg"
                      type="number"
                      className="form-control"
                      value={cfg}
                      onChange={(e) => setCfg(parseFloat(e.target.value) || 0)}
                      min="1"
                      max="20"
                      step="0.1"
                    />
                    <small className="text-muted d-block mt-2">Classifier-free guidance scale</small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="samplerName" className="form-label fw-bold">
                      Sampler
                    </label>
                    <input
                      id="samplerName"
                      type="text"
                      className="form-control"
                      value={samplerName}
                      onChange={(e) => setSamplerName(e.target.value)}
                      placeholder="e.g., euler, dpmpp_2m"
                    />
                    <small className="text-muted d-block mt-2">Sampler name (e.g., euler, dpmpp_2m)</small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="scheduler" className="form-label fw-bold">
                      Scheduler
                    </label>
                    <input
                      id="scheduler"
                      type="text"
                      className="form-control"
                      value={scheduler}
                      onChange={(e) => setScheduler(e.target.value)}
                      placeholder="e.g., simple, karras"
                    />
                    <small className="text-muted d-block mt-2">Scheduler type (e.g., simple, karras)</small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="denoise" className="form-label fw-bold">
                      Denoise
                    </label>
                    <input
                      id="denoise"
                      type="number"
                      className="form-control"
                      value={denoise}
                      onChange={(e) => setDenoise(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="1"
                      step="0.1"
                    />
                    <small className="text-muted d-block mt-2">Denoising strength (0-1)</small>
                  </div>

                  <div className="col-md-6 mb-0">
                    <label htmlFor="fluxGuidance" className="form-label fw-bold">
                      Flux Guidance
                    </label>
                    <input
                      id="fluxGuidance"
                      type="number"
                      className="form-control"
                      value={fluxGuidance}
                      onChange={(e) => setFluxGuidance(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="10"
                      step="0.1"
                    />
                    <small className="text-muted d-block mt-2">Flux model guidance strength</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Image Paste Box */}
            <div className="card mb-4 border-2 border-dashed">
              <div
                ref={pasteBoxRef}
                className="card-body p-5 text-center cursor-pointer hover-effect"
                onPaste={handlePaste}
                tabIndex={0}
                style={{
                  cursor: 'pointer',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {imageBase64 ? (
                  <div className="w-100">
                    <img 
                      src={imageBase64} 
                      alt="Pasted" 
                      className="img-thumbnail mb-3"
                      style={{ maxHeight: '200px', maxWidth: '100%' }}
                    />
                    <p className="text-muted small">Image pasted. Ready to generate!</p>
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={() => {
                        setImageBase64(null);
                        setMaskBase64(null);
                      }}
                    >
                      Clear Image
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={handlePasteFromClipboard}
                    >
                      Paste Another
                    </button>
                  </div>
                ) : (
                  <div>
                    <h5 className="mb-2">📋 Paste an image here</h5>
                    <p className="text-muted mb-0">Use Ctrl+V (or Cmd+V) to paste an image</p>
                    <p className="text-muted small mb-3">(Optional - not required to generate)</p>
                    <button
                      className="btn btn-outline-primary"
                      onClick={handlePasteFromClipboard}
                    >
                      📱 Paste from Clipboard
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mask Editor for Inpainting */}
            {apiFormat === 'ComfyUI-Inpaint' && imageBase64 && (
              <div className="card mb-4 border-0 bg-white">
                <div className="card-body">
                  <h5 className="card-title mb-3">Mask Editor</h5>
                  <p className="text-muted small mb-3">
                    Draw on the image to create a mask. White areas will be inpainted, black areas will remain unchanged.
                  </p>
                  <MaskEditor 
                    imageBase64={imageBase64} 
                    onMaskChange={setMaskBase64}
                  />
                </div>
              </div>
            )}

            {/* Prompt Input */}
            <div className="mb-4">
              <label htmlFor="prompt" className="form-label fw-bold">
                Prompt
              </label>
              <textarea
                id="prompt"
                className="form-control"
                rows="4"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe what you want to generate..."
              />
              <small className="text-muted d-block mt-2">
                Be descriptive and detailed for better results
              </small>
              
              {/* Save Prompt Section */}
              <div className="mt-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Prompt name..."
                    value={promptName}
                    onChange={(e) => setPromptName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSavePrompt()}
                  />
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleSavePrompt}
                    title="Save this prompt for later use"
                  >
                    💾 Save Prompt
                  </button>
                </div>
              </div>
            </div>

            {/* Negative Prompt Input */}
            <div className="mb-4">
              <label htmlFor="negativePrompt" className="form-label fw-bold">
                Negative Prompt (Optional)
              </label>
              <textarea
                id="negativePrompt"
                className="form-control"
                rows="2"
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder="Things you don't want in the image..."
              />
            </div>

            {/* Saved Prompts Section */}
            {savedPrompts.length > 0 && (
              <div className="card mb-4 border-0 bg-white">
                <div className="card-body">
                  <h5 className="card-title mb-3">📚 Saved Prompts</h5>
                  <div className="list-group">
                    {savedPrompts.map((savedPrompt) => (
                      <div key={savedPrompt.id} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <h6 className="mb-1">{savedPrompt.name}</h6>
                            <p className="text-muted small mb-1">{savedPrompt.prompt}</p>
                            {savedPrompt.negativePrompt && (
                              <p className="text-muted small mb-1"><em>Negative: {savedPrompt.negativePrompt}</em></p>
                            )}
                            <small className="text-muted d-block">{savedPrompt.createdAt}</small>
                          </div>
                          <div className="ms-2">
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleLoadPrompt(savedPrompt)}
                              title="Load this prompt"
                            >
                              📥 Load
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeletePrompt(savedPrompt.id)}
                              title="Delete this prompt"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {/* Job Control Section */}
            {jobId && (
              <div className="card mb-4 border-0 bg-white">
                <div className="card-body">
                  <h5 className="card-title mb-3">Job Control</h5>
                  <div className="mb-3">
                    <label htmlFor="currentJobId" className="form-label fw-bold">
                      Current Job ID
                    </label>
                    <input
                      id="currentJobId"
                      type="text"
                      className="form-control"
                      value={jobId}
                      onChange={(e) => setJobId(e.target.value)}
                      readOnly
                    />
                  </div>
                  <button
                    className="btn btn-sm btn-outline-warning me-2"
                    onClick={() => {
                      setJobId(null);
                      setRuncomfyResultUrl(null);
                      setPollingStatus(null);
                    }}
                  >
                    Clear Job
                  </button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {error}
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setError(null)}
                />
              </div>
            )}

            {/* Generate Button */}
            {!jobId ? (
              <div className="d-grid gap-2 mb-4">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleGenerateImage}
                  disabled={
                    loading || 
                    !prompt.trim() || 
                    !apiUrl.trim() || 
                    !token.trim() ||
                    (apiFormat === 'ComfyUI-i2i' && !imageBase64) ||
                    (apiFormat === 'ComfyUI-Inpaint' && (!imageBase64 || !maskBase64)) ||
                    (apiFormat === 'ComfyUI-Qwen' && !imageBase64) ||
                    (apiFormat === 'RunComfy-Qwen' && (!imageBase64 || !runcomfyDeploymentId.trim()))
                  }
                >
                  {loading ? 'Generating...' : 'Generate Image'}
                </button>
              </div>
            ) : (
              <div className="d-grid gap-2 mb-4">
                <button
                  className="btn btn-info btn-lg"
                  onClick={handlePollStatus}
                  disabled={loading}
                >
                  {loading ? 'Checking Status...' : 'Check Status'}
                </button>
              </div>
            )}

            {/* Job Status Message */}
            {jobId && pollingStatus && (
              <div className="alert alert-info mb-4">
                <p className="mb-1 small">Status: <code>{pollingStatus}</code> | Job ID: <code className="text-break">{jobId}</code></p>
                <p className="mb-0 small">Time elapsed: <strong>{elapsedSincePost}s</strong></p>
              </div>
            )}

            {/* Timer */}
            {loading && (
              <div className="text-center mb-4">
                <div className="spinner-border text-primary me-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="d-inline">
                  <p className="mb-0 fw-bold">Elapsed time: <span className="text-primary">{formatTime(elapsedTime)}</span></p>
                  <p className="text-muted small mb-0">Please wait, this may take a minute...</p>
                </div>
              </div>
            )}

            {/* Response Image */}
            {responseImage && !loading && !jobId && (
              <div className="card mt-5">
                <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">✅ Generated Image</h5>
                  {imageBase64 && (
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className={`btn ${!showOriginal ? 'btn-light' : 'btn-outline-light'}`}
                        onClick={() => setShowOriginal(false)}
                        title="View generated image"
                      >
                        Generated
                      </button>
                      <button
                        type="button"
                        className={`btn ${showOriginal ? 'btn-light' : 'btn-outline-light'}`}
                        onClick={() => setShowOriginal(true)}
                        title="View original image"
                      >
                        Original
                      </button>
                    </div>
                  )}
                </div>
                <div className="card-body p-0">
                  <img 
                    src={showOriginal ? imageBase64 : responseImage} 
                    alt={showOriginal ? "Original" : "Generated"} 
                    className="img-fluid w-100"
                    style={{ maxHeight: '600px', objectFit: 'contain' }}
                  />
                </div>
              </div>
            )}

            {/* Job History Section */}
            {jobHistory.length > 0 && (
              <div className="mt-5">
                <h3 className="mb-4">📜 Job History</h3>
                <div className="row">
                  {jobHistory.map((historyJob) => (
                    <div key={historyJob.id} className="col-12 col-md-6 col-lg-4 mb-4">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <h6 className="mb-1">{historyJob.timestamp}</h6>
                            <small>{historyJob.apiFormat}</small>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-light"
                            onClick={() => deleteJobFromHistory(historyJob.id)}
                            title="Delete this job"
                          >
                            ✕
                          </button>
                        </div>
                        {historyJob.resultImage ? (
                          <div className="card-body p-0">
                            <img 
                              src={historyJob.resultImage} 
                              alt="Generated" 
                              className="img-fluid w-100"
                              style={{ maxHeight: '300px', objectFit: 'cover' }}
                            />
                          </div>
                        ) : (
                          <div className="card-body d-flex flex-column justify-content-center align-items-center text-center" style={{ minHeight: '300px' }}>
                            <div className="spinner-border text-info mb-2" role="status" style={{ width: '2rem', height: '2rem' }}>
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted small mb-0">
                              <code className="text-break">{historyJob.id.substring(0, 12)}...</code>
                            </p>
                            <p className="text-muted small mt-2">Status: <strong>{historyJob.status}</strong></p>
                          </div>
                        )}
                        <div className="card-footer bg-light">
                          <small className="text-muted d-block">
                            Job ID: <code className="text-break">{historyJob.id}</code>
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;

